import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, MapPin, Users, Zap, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const location = useLocation();

  React.useEffect(() => {
    document.title = 'About Us - Zeus Weather';
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <img
                src="https://customer-assets.emergentagent.com/job_522c5977-b846-493f-b349-2811165f83cd/artifacts/ch6dtlgq_logo.png"
                alt="Zeus Weather"
                className="h-10 w-auto" />

            </Link>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => window.open('https://portal.getzeusweather.com', '_blank')}
                className="text-slate-700 hover:text-slate-900">

                Login
              </Button>
              <Link to="/">
                <Button variant="ghost" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Built From the Roofer's Perspective
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              Zeus Weather was born from a simple frustration: storm targeting shouldn't be this hard.
            </p>
            <p className="text-lg text-blue-200 mt-4 leading-relaxed">
              Not because roofers don't work hard — but because they're forced to juggle too many disconnected tools, 
              outdated data, and manual processes when timing matters most.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">Our Story</h2>
            
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
              <div>
                <div className="bg-blue-50 p-8 rounded-lg border-2 border-blue-200">
                  <p className="text-2xl font-bold text-slate-900 mb-4">
                    The idea for Zeus didn't start in a boardroom or a weather lab.
                  </p>
                  <p className="text-xl text-slate-700">
                    It started with a conversation.
                  </p>
                </div>
              </div>
              
              <div>
                <p className="text-lg text-slate-700 leading-relaxed mb-4">
                  <strong className="text-xl text-blue-600">My brother is a roofer.</strong>
                </p>
                <p className="text-lg text-slate-700 leading-relaxed">
                  After every major storm, I'd hear the same complaints. As a technology and process builder, 
                  those frustrations stuck with me.
                </p>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-slate-900 to-blue-900 p-8 md:p-12 rounded-lg text-white mb-12">
              <h3 className="text-2xl font-bold mb-6 text-center">The Problems We Kept Hearing</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
                  <p className="text-lg italic">"We never know which neighborhoods actually got hit."</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
                  <p className="text-lg italic">"By the time we buy lists, they're already outdated."</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
                  <p className="text-lg italic">"We're switching between weather apps, canvassing tools, spreadsheets, texts, and emails."</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
                  <p className="text-lg italic">"The data is there — it's just scattered and slow."</p>
                </div>
              </div>
            </div>
            
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-block bg-red-50 border-l-4 border-red-600 p-8 rounded-r-lg mb-8">
                <p className="text-2xl font-bold text-slate-900 leading-relaxed">
                  Roofers weren't losing jobs because they lacked hustle.<br />
                  <span className="text-red-600">They were losing jobs because the systems around storm response were broken.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Built for Simplicity */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Built for Simplicity, Not Complexity</h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Zeus Weather was built from the roofer's point of view, not from the perspective of weather data providers or software vendors.
              </p>
            </div>
            
            <div className="bg-white p-8 md:p-12 rounded-lg shadow-lg border-2 border-slate-200 mb-12">
              <p className="text-2xl font-bold text-center text-slate-900 mb-8">
                Three Critical Questions Every Roofer Needs Answered After a Storm
              </p>
              
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="border-2 border-blue-300 bg-gradient-to-br from-blue-50 to-white">
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-full text-2xl font-bold mb-4">
                      1
                    </div>
                    <p className="text-lg font-semibold text-slate-900">Where did real damage actually occur?</p>
                  </CardContent>
                </Card>
                
                <Card className="border-2 border-blue-300 bg-gradient-to-br from-blue-50 to-white">
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-full text-2xl font-bold mb-4">
                      2
                    </div>
                    <p className="text-lg font-semibold text-slate-900">Which homes are worth knocking first?</p>
                  </CardContent>
                </Card>
                
                <Card className="border-2 border-blue-300 bg-gradient-to-br from-blue-50 to-white">
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-full text-2xl font-bold mb-4">
                      3
                    </div>
                    <p className="text-lg font-semibold text-slate-900">How do I get my crews there faster — without juggling tools?</p>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-xl text-slate-900 font-semibold mb-6">
                Everything in Zeus exists to remove friction:
              </p>
              
              <div className="inline-grid grid-cols-2 gap-4 text-left">
                <div className="flex items-center space-x-3 bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 text-xl font-bold">✓</span>
                  </div>
                  <span className="text-lg text-slate-700">Fewer logins</span>
                </div>
                <div className="flex items-center space-x-3 bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 text-xl font-bold">✓</span>
                  </div>
                  <span className="text-lg text-slate-700">Fewer tools</span>
                </div>
                <div className="flex items-center space-x-3 bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 text-xl font-bold">✓</span>
                  </div>
                  <span className="text-lg text-slate-700">Fewer guesses</span>
                </div>
                <div className="flex items-center space-x-3 bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 text-xl font-bold">✓</span>
                  </div>
                  <span className="text-lg text-slate-700">Faster action</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Zeus Is Different */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Why Zeus Is Different</h2>
              <div className="max-w-2xl mx-auto">
                <p className="text-2xl font-bold text-slate-900 mb-2">
                  Most storm tools are built around data.
                </p>
                <p className="text-2xl font-bold text-blue-600">
                  Zeus is built around the workflow.
                </p>
              </div>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-lg border-2 border-blue-200">
                <h3 className="text-xl font-bold text-slate-900 mb-4">We Adapt to You</h3>
                <p className="text-lg text-slate-700 mb-6">
                  Instead of forcing roofers to adapt to complex software, Zeus adapts to how storm-response teams actually work:
                </p>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                      <span className="text-white text-xl">🚗</span>
                    </div>
                    <span className="text-lg text-slate-700 font-semibold">Crews in trucks</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                      <span className="text-white text-xl">📱</span>
                    </div>
                    <span className="!font-semibold !text-lg text-slate-700">Canvassers in neighborhoods</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                      <span className="text-white text-xl">⚡</span>
                    </div>
                    <span className="text-lg text-slate-700 font-semibold">Fast decisions under pressure</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-slate-900 to-blue-900 p-8 rounded-lg text-white">
                <h3 className="text-xl font-bold mb-4">One Platform, Everything You Need</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                    <span className="text-green-400 text-xl">✓</span>
                    <span className="text-lg">Storm targeting</span>
                  </div>
                  <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                    <span className="text-green-400 text-xl">✓</span>
                    <span className="text-lg">Canvassing</span>
                  </div>
                  <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                    <span className="text-green-400 text-xl">✓</span>
                    <span className="text-lg">Performance visibility</span>
                  </div>
                  <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                    <span className="text-green-400 text-xl">✓</span>
                    <span className="text-lg">Job-site documentation</span>
                  </div>
                  <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                    <span className="text-green-400 text-xl">✓</span>
                    <span className="text-lg">Automated follow-ups</span>
                  </div>
                </div>
                <p className="text-blue-200 mt-6 text-center font-semibold">
                  All without constant app switching.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Drives Us */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">What Drives Us</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-2 border-slate-200">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Users className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3">Roofer-First Perspective</h3>
                      <p className="text-slate-700 leading-relaxed">
                        Every decision starts with one question: "Would this make storm response easier for a roofer in the field?" 
                        If the answer is no, it doesn't ship.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-slate-200">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Zap className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3">Speed Without Guessing</h3>
                      <p className="text-slate-700 leading-relaxed">
                        Being first matters — but only if you're first in the right places. We focus on delivering accurate, 
                        actionable storm data quickly so teams can move with confidence.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-slate-200">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Target className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3">Simplicity Wins</h3>
                      <p className="text-slate-700 leading-relaxed">
                        If a tool needs training manuals, it's already failed. Zeus is designed to be intuitive enough that crews 
                        can use it immediately — even under pressure.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-slate-200">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <MapPin className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3">Fewer Tools, Better Results</h3>
                      <p className="text-slate-700 leading-relaxed">
                        Roofers already have enough subscriptions. Zeus exists to replace complexity with clarity and help teams 
                        do more with less.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Who We Are</h2>
            </div>
            
            <div className="bg-slate-50 p-8 rounded-lg border-2 border-slate-200">
              <p className="text-slate-700 leading-relaxed mb-6 text-lg">
                Zeus Weather is built by a team of:
              </p>
              
              <ul className="space-y-3 text-lg text-slate-700 mb-6">
                <li className="flex items-center">
                  <span className="text-blue-600 mr-3">•</span>
                  Technology and process experts
                </li>
                <li className="flex items-center">
                  <span className="text-blue-600 mr-3">•</span>
                  Data engineers
                </li>
                <li className="flex items-center">
                  <span className="text-blue-600 mr-3">•</span>
                  Industry partners who understand storm-response roofing
                </li>
              </ul>
              
              <p className="text-slate-700 leading-relaxed mb-6 text-lg">
                The platform was founded by <strong>Joe Parker</strong>, who specializes in building systems that eliminate 
                inefficiency and bring order to complex workflows.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg">
                We've spent years listening to roofers, observing storm response in action, and refining Zeus based on 
                real-world feedback — not theoretical use cases.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Where We're Going */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Where We're Going</h2>
            
            <p className="text-xl text-slate-700 leading-relaxed mb-6">
              Today, Zeus Weather supports storm-response roofing teams across the United States.<br />
              But this is just the beginning.
            </p>
            
            <div className="bg-blue-50 p-8 rounded-lg border-2 border-blue-200">
              <p className="text-2xl font-bold text-slate-900 mb-4">Our mission is simple:</p>
              <p className="text-xl text-slate-700 leading-relaxed">
                Make storm targeting easier, faster, and more effective — so roofers can focus on closing jobs, not fighting software.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Ready to See Zeus in Action?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Join hundreds of roofing contractors using Zeus to simplify storm response and win more jobs.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => window.open('https://portal.getzeusweather.com/free_plan', '_blank')}
                className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6">

                Start Your 30-Day Free Trial
              </Button>
              <Button
                size="lg"
                onClick={() => window.open('https://sites.leadconnectorhq.com/preview/wcmdWIrxpgWpyny0G977', '_blank')}
                className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6">

                See Zeus in Action
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_zeus-roofer-app/artifacts/7vhqji80_logo1-removebg-preview%20%281%29.png" 
                alt="Zeus Weather" 
                className="h-10 w-auto mb-4"
              />
              <p className="text-sm text-slate-400">
                Sales Operating System for Roofing Companies
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/#benefits" className="hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/#pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><a href="https://sites.leadconnectorhq.com/preview/wcmdWIrxpgWpyny0G977" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Demo</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
                <li><a href="https://blog.getzeusweather.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Blog</a></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Support</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-400">
            <p>&copy; 2025 Zeus Weather. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;